import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Slack, Search, Lock, Users } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Skeleton } from './Skeleton';

export default function SlackChannelSelector({ sourceConnId, open, onOpenChange, onSelect }) {
  const [channels, setChannels] = useState([]);
  const [filteredChannels, setFilteredChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [warning, setWarning] = useState(null);

  useEffect(() => {
    if (open && sourceConnId) {
      fetchChannels();
    } else {
      // Reset state when dialog closes
      setChannels([]);
      setFilteredChannels([]);
      setSearchQuery('');
      setWarning(null);
    }
  }, [open, sourceConnId]);

  useEffect(() => {
    // Filter channels based on search query
    if (!searchQuery.trim()) {
      setFilteredChannels(channels);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredChannels(
        channels.filter(
          (channel) =>
            channel.name.toLowerCase().includes(query) ||
            channel.topic.toLowerCase().includes(query) ||
            channel.purpose.toLowerCase().includes(query)
        )
      );
    }
  }, [searchQuery, channels]);

  const fetchChannels = async () => {
    setLoading(true);
    setWarning(null);
    try {
      const response = await api.get(`/integrations/slack/${sourceConnId}/channels`);
      const channelsList = response.data.channels || [];
      setChannels(channelsList);
      setFilteredChannels(channelsList);
      
      // Show warning if present (e.g., only public channels available)
      if (response.data.warning) {
        setWarning(response.data.warning);
        toast.warning(response.data.warning, { duration: 6000 });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to fetch Slack channels';
      toast.error(errorMsg);
      console.error('Error fetching Slack channels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChannel = (channel) => {
    onSelect({
      channel_id: channel.id,
      channel_name: channel.name,
      filter_value: channel.id,
      filter_label: `#${channel.name}`,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Slack className="h-5 w-5" />
            Select Slack Channel
          </DialogTitle>
          <DialogDescription>
            Choose a channel to add as a source for this project
          </DialogDescription>
        </DialogHeader>

        {/* Warning Banner */}
        {warning && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3 text-sm">
            <p className="text-yellow-800 dark:text-yellow-200">
              ⚠️ {warning}
            </p>
          </div>
        )}

        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search channels..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Channels List */}
        <div className="flex-1 overflow-y-auto min-h-[300px] max-h-[400px] space-y-2 py-4">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredChannels.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground">
                {searchQuery ? 'No channels found matching your search' : 'No channels available'}
              </p>
            </Card>
          ) : (
            filteredChannels.map((channel) => (
              <Card
                key={channel.id}
                className="p-4 cursor-pointer hover:bg-accent transition-colors"
                onClick={() => handleSelectChannel(channel)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">#{channel.name}</span>
                      {channel.is_private && (
                        <Badge variant="secondary" className="text-xs">
                          <Lock className="h-3 w-3 mr-1" />
                          Private
                        </Badge>
                      )}
                    </div>
                    {channel.topic && (
                      <p className="text-sm text-muted-foreground mb-1">{channel.topic}</p>
                    )}
                    {channel.purpose && (
                      <p className="text-xs text-muted-foreground">{channel.purpose}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>{channel.num_members}</span>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

